import * as React from "react";
import { DropdownMenu as DropdownMenuPrimitive } from "radix-ui";

import { camadaDeVidroClass } from "../../lib/superficies";
import { cn } from "../../lib/utils";

/**
 * TUT-563a (Lumen 2.0) — o MENU de ações: o ⋯ da linha e o ⋯ do cabeçalho da
 * ficha. É o `DropdownMenu` do Radix (`@radix-ui/react-dropdown-menu`, que vem
 * do `radix-ui` já instalado: zero dependência nova).
 *
 * Por que ele, e não o `MenuDaLinha` sobre o `Popover`: o papel `menu` promete
 * setas, Home/End, busca por letra e foco itinerante, e o `Popover` não cumpria
 * nada disso. O Radix cumpre, devolve o foco ao gatilho e fecha no `onSelect`.
 * Descartados (o plano, item 4): escrever as setas à mão sobre o `Popover`
 * (reinventar o Radix, sem o teste do fornecedor) e o `Command` da paleta
 * (busca, não menu).
 *
 * O conteúdo é CAMADA do Lumen (`camadaDeVidroClass`: vidro forte com
 * desfoque), como o `Popover` (P34).
 */

function DropdownMenu(props: React.ComponentProps<typeof DropdownMenuPrimitive.Root>) {
  return <DropdownMenuPrimitive.Root data-slot="dropdown-menu" {...props} />;
}

function DropdownMenuTrigger(props: React.ComponentProps<typeof DropdownMenuPrimitive.Trigger>) {
  return <DropdownMenuPrimitive.Trigger data-slot="dropdown-menu-trigger" {...props} />;
}

function DropdownMenuContent({
  className,
  align = "end",
  sideOffset = 6,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Content>) {
  return (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.Content
        data-slot="dropdown-menu-content"
        align={align}
        sideOffset={sideOffset}
        className={cn(
          "z-50 grid min-w-[12rem] origin-(--radix-dropdown-menu-content-transform-origin) gap-0.5 rounded-[var(--radius-bloco)] border p-1 text-popover-foreground shadow-[var(--shadow-md)] outline-hidden",
          camadaDeVidroClass,
          "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=closed]:duration-[var(--dur-fast)]",
          "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=open]:duration-[var(--dur-fast)] motion-reduce:animate-none",
          className,
        )}
        {...props}
      />
    </DropdownMenuPrimitive.Portal>
  );
}

function DropdownMenuItem({
  className,
  perigo = false,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Item> & {
  /** O item do grupo de perigo (Excluir, Revogar): o texto na cor de perigo. */
  perigo?: boolean;
}) {
  return (
    <DropdownMenuPrimitive.Item
      data-slot="dropdown-menu-item"
      {...(perigo ? { "data-perigo": "" } : {})}
      className={cn(
        "relative flex min-h-[var(--control-h-compact)] cursor-pointer items-center gap-2 rounded-[calc(var(--radius-botao)-4px)] px-2.5 text-corpo font-botao text-foreground outline-hidden select-none",
        "data-[highlighted]:bg-[var(--hover)] data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        "data-[perigo]:text-destructive data-[perigo]:data-[highlighted]:bg-[color-mix(in_srgb,var(--destructive)_12%,transparent)]",
        "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    />
  );
}

function DropdownMenuSeparator({ className, ...props }: React.ComponentProps<typeof DropdownMenuPrimitive.Separator>) {
  return (
    <DropdownMenuPrimitive.Separator
      data-slot="dropdown-menu-separator"
      className={cn("-mx-1 my-1 h-px bg-border", className)}
      {...props}
    />
  );
}

function DropdownMenuLabel({ className, ...props }: React.ComponentProps<typeof DropdownMenuPrimitive.Label>) {
  return (
    <DropdownMenuPrimitive.Label
      data-slot="dropdown-menu-label"
      className={cn("px-2.5 py-1.5 text-legenda font-botao text-[var(--muted)]", className)}
      {...props}
    />
  );
}

export {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
};
